import { Api } from "~/api/index";
import type {
  IAuctionItem,
  IMarketplaceFilter,
  IMarketplacePrimary,
  IPagination,
} from "~/common/interfaces/IMarketplace";
import type { CreateOrderInput } from "@opensea/seaport-js/src/types";
import type { OrderWithCounter } from "@opensea/seaport-js/lib/types";
import { type CardType, MarketType } from "fungi-types";
import { useStoreUserV2 } from "~/stores/storeUserV2";

export class MarketplaceApi extends Api {
  constructor(private readonly type: MarketType) {
    super(`marketplace/${type}`);
  }

  public async getCards(
    filters: IMarketplaceFilter,
    pagination?: IPagination
  ): Promise<IMarketplacePrimary> {
    const { walletAddress } = useStoreUserV2();
    try {
      const customParams = new URLSearchParams(window.location.search);
      const privateSale = customParams.get("privatesale");
      const walletAddress = customParams.get("seller");

      const { data } = await this.api.get<IMarketplacePrimary>("", {
        params: {
          player_name: filters.player_name,
          price: filters.price?.join(","),
          scarcity: filters.scarcity?.join(","),
          skill: filters.skill?.join(","),
          skill_score: filters.skill_score?.join(","),
          skill_level: filters.skill_level?.join(","),
          court_type: filters.court_type?.join(","),
          gender: filters.gender?.join(","),
          age: filters.age?.join(","),
          country_iso: filters.country_iso?.map((e) => e.toUpperCase()).join(","),
          bidderWallet: filters.showMyBids ? walletAddress : undefined,
          sort: filters.sort,
          edition: filters.edition?.join(","),
          season: filters.season?.join(","),
          xpLevel: filters.xpLevel?.join(","),
          tennisPlayerId: filters.tennisPlayerId,
          myFavoritesOnly: filters.myFavoritesOnly,
          auctionId: filters.auctionId,
          // Private sale to be remove when sales are done
          privatesale: privateSale,
          sellerWallet: walletAddress,
          // PAGINATION
          page: pagination?.page,
          per_page: pagination?.per_page,
        },
      });
      return data;
    } catch (e) {
      throw "Unable to get cards";
    }
  }

  public async listingParams(
    tokenId: number,
    amount: string,
    userToken: string,
    cardType: CardType
  ): Promise<CreateOrderInput> {
    if (this.type !== MarketType.SECONDARY) {
      throw "Unable to list";
    }
    try {
      const { data } = await this.api.get<CreateOrderInput>("/list", {
        params: {
          tokenId,
          amount,
          cardType,
        },
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      });
      return data;
    } catch (e) {
      throw "Unable to list";
    }
  }

  public async list(
    orderParams: OrderWithCounter,
    tokenId: number,
    userToken: string,
    cardType: CardType
  ): Promise<{ success: boolean; auction: IAuctionItem | null }> {
    if (this.type !== MarketType.SECONDARY) {
      throw "Unable to list";
    }
    const { data } = await this.api.post(
      "/list",
      {
        tokenId: `${tokenId}`,
        ...orderParams,
        cardType,
      },
      {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
    return data;
  }

  public async buy(cuid: string): Promise<any> {
    if (this.type !== MarketType.SECONDARY) {
      throw "Unable to buy";
    }
    try {
      const { data } = await this.api.post("/buy", { cuid: cuid });
      return data;
    } catch (e) {
      console.log(e);
      throw "Unable to buy";
    }
  }

  public async unlist(cuid: string): Promise<{ success: boolean }> {
    if (this.type !== MarketType.SECONDARY) {
      throw "Unable to buy";
    }
    try {
      const { data } = await this.api.post("/unlist", { cuid: cuid });
      return data;
    } catch (e) {
      console.log(e);
      throw "Unable to buy";
    }
  }
}
